// @flow
import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import styles from './share.module.scss'
import EmbedModal from '@components/modals/embed'

interface Props {
  type: 'sound' | 'album'
  id: string
  url: string
  text: string
}

// eslint-disable-next-line max-len
const shareOption =
  'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'

const Share = ({ type, id, url, text }: Props) => {
  const intl = useIntl()
  const [ isShareable, setShareable ] = useState(false)
  const [ isEmbedOpen, setEmbedOpen ] = useState(false)

  const onClick = (e) => {
    e.preventDefault()
    const { href } = e.currentTarget
    if (!href) {
      return
    }
    window.open(href, 'share', shareOption)
  }

  // for mobile, this can be optional.
  const onShareClick = () => {
    navigator.share({ url, title: document.title, text })
  }

  useEffect(() => setShareable(typeof navigator.share === 'function'), [])

  return (
    <div className="d-flex d-md-inline-block align-items-center">
      <div className="d-inline-block tx-18 tx-oswald tx-w500 tx-g700 mr-3 align-middle">
        SHARE
      </div>
      <a
        className={`${styles.twitter} mr-2`}
        href={`https://twitter.com/intent/tweet?text=${text}&url=${url}&via=soundonlive`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={onClick}
        aria-label={intl.formatMessage(messages.twitter)}
      >
        <span className={`${styles.icon} solicon-twitter-alt`} />
        <span className={styles.text}>
          <FormattedMessage {...messages.twitter} />
        </span>
      </a>
      <a
        className={`${styles.facebook} mr-2`}
        href={`https://www.facebook.com/sharer.php?u=${url}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={onClick}
        aria-label={intl.formatMessage(messages.facebook)}
      >
        <span className={`${styles.icon} solicon-facebook`} />
        <span className={styles.text}>
          <FormattedMessage {...messages.facebook} />
        </span>
      </a>
      <a
        className={`${styles.line} mr-2`}
        href={`http://line.me/R/msg/text/?${text}%20${url}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={onClick}
        aria-label={intl.formatMessage(messages.line)}
      >
        <img
          src="/static/images/icons/line.svg"
          alt="line"
          className={styles.icon}
        />
        <span className={styles.text}>
          <FormattedMessage {...messages.line} />
        </span>
      </a>
      <button
        type="button"
        className={`btn-chromeless ${styles.widget}`}
        onClick={() => setEmbedOpen(true)}
        aria-label={intl.formatMessage(messages.link)}
      >
        <span className={`${styles.icon} solicon-link`} />
        <span className={styles.text}>
          <FormattedMessage {...messages.link} />
        </span>
      </button>
      {isShareable && (
        <button
          type="button"
          className={`btn-chromeless ${styles.share} ml-2`}
          onClick={onShareClick}
          aria-label={intl.formatMessage(messages.share)}
        >
          <span className={`${styles.icon} solicon-export`} />
          <span className={styles.text}>
            <FormattedMessage {...messages.share} />
          </span>
        </button>
      )}
      <EmbedModal
        isOpen={isEmbedOpen}
        type={type}
        id={id}
        url={url}
        onClose={() => setEmbedOpen(false)}
      />
    </div>
  )
}

export default Share
